import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { aiAgentAPI, messagesAPI } from '../../lib/api';

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [aiEnabled, setAiEnabled] = useState(true);
  const [recentSessions, setRecentSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError('');
      const [statsRes, aiStatusRes, sessionsRes] = await Promise.all([
        messagesAPI.getDashboardStats(),
        aiAgentAPI.getGlobalStatus().catch(() => null),
        messagesAPI.getAllSessions({ limit: 6, page: 1 }).catch(() => null)
      ]);
      setStats(statsRes?.data?.stats || null);
      setAiEnabled(
        aiStatusRes?.data?.aiEnabled ??
        aiStatusRes?.data?.globalAIEnabled ??
        true
      );
      setRecentSessions(sessionsRes?.data?.sessions || []);
    } catch (_err) {
      setError('فشل تحميل بيانات لوحة التحكم');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, []);

  const formatNumber = (value) => Number(value || 0).toLocaleString();
  const formatPlatformName = (platform = '') => {
    const names = { telegram: 'تيليجرام', whatsapp: 'واتساب', facebook: 'فيسبوك', instagram: 'إنستغرام', web: 'ويب', other: 'أخرى' };
    return names[platform] || platform || 'غير معروف';
  };

  const relativeTime = (value) => {
    if (!value) return '';
    const timestamp = new Date(value).getTime();
    if (Number.isNaN(timestamp)) return '';
    const diffMs = Date.now() - timestamp;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'الآن';
    if (diffMin < 60) return `منذ ${diffMin} د`;
    const diffHour = Math.floor(diffMin / 60);
    if (diffHour < 24) return `منذ ${diffHour} س`;
    const diffDay = Math.floor(diffHour / 24);
    return `منذ ${diffDay} ي`;
  };

  const kpiCards = useMemo(() => ([
    { label: 'إجمالي الجلسات', value: stats?.sessions?.total ?? 0, accent: 'bg-cyan-500/10 text-cyan-300' },
    { label: 'الجلسات النشطة', value: stats?.sessions?.active ?? 0, accent: 'bg-emerald-500/10 text-emerald-300' },
    { label: 'الرسائل اليوم', value: stats?.messages?.today ?? 0, accent: 'bg-blue-500/10 text-blue-300' },
    { label: 'إجمالي الرسائل', value: stats?.messages?.total ?? 0, accent: 'bg-violet-500/10 text-violet-300' },
    { label: 'جلسات جديدة اليوم', value: stats?.sessions?.today ?? 0, accent: 'bg-amber-500/10 text-amber-300' },
    { label: 'متوسط رسائل/جلسة', value: stats?.messages?.avgPerSession ?? 0, accent: 'bg-pink-500/10 text-pink-300' }
  ]), [stats]);

  const topSessions = stats?.topSessions || [];
  const sessionsByPlatform = stats?.sessions?.byPlatform || {};
  const totalSessions = stats?.sessions?.total || 0;
  const dailyTrend = stats?.messages?.dailyTrend || [];
  const maxTrend = dailyTrend.length > 0 ? Math.max(...dailyTrend.map((item) => Number(item.count) || 0)) : 0;

  if (loading) {
    return (
        <div className="h-full w-full dark:bg-dark_bg_4 select-none border-s dark:border-s-dark_border_2 border-b-[6px] border-b-green_2 flex items-center justify-center">
        <p className="text-dark_text_2">جاري تحميل لوحة التحكم...</p>
      </div>
    );
  }

  if (error || !stats) {
    return (
        <div className="h-full w-full dark:bg-dark_bg_4 select-none border-s dark:border-s-dark_border_2 border-b-[6px] border-b-green_2 flex items-center justify-center">
        <div className="text-center">
          <p className="text-dark_text_1">{error || 'لا توجد بيانات'}</p>
          <button onClick={fetchDashboardData} className="mt-3 px-4 py-2 rounded-lg bg-green_1 hover:bg-green_2 text-white">
            إعادة المحاولة
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full dark:bg-dark_bg_4 select-none border-s dark:border-s-dark_border_2 border-b-[6px] border-b-green_2 overflow-auto">
      <div className="p-4 sm:p-6 space-y-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-lg sm:text-[22px] font-semibold text-dark_text_1">لوحة التحكم</h2>
            <p className="text-sm text-dark_text_2 mt-1">نظرة عامة على أداء الذكاء الاصطناعي والرسائل</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={fetchDashboardData} className="px-3 py-1.5 rounded-lg bg-dark_bg_3 hover:bg-dark_hover_1 border border-dark_border_2 text-dark_text_1 text-sm font-medium">
              تحديث
            </button>
            <button onClick={() => navigate('/app/inbox')} className="px-3 py-2 rounded-lg bg-green_1 hover:bg-green_2 text-white">
              فتح الوارد
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-3">
          {kpiCards.map((card) => (
            <div key={card.label} className="card p-4">
              <div className="flex items-center justify-between">
                <p className="text-dark_text_2 text-xs uppercase tracking-wide">{card.label}</p>
                <span className={`text-[11px] px-2 py-0.5 rounded-full ${card.accent}`}>KPI</span>
              </div>
              <p className="text-3xl font-semibold mt-3 text-dark_text_1">{formatNumber(card.value)}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 2xl:grid-cols-3 gap-4">
          <div className="2xl:col-span-2 card p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-dark_text_1 font-medium">حالة الذكاء الاصطناعي</h3>
              <span className={`text-xs px-2 py-1 rounded-full ${aiEnabled ? 'bg-green_3 text-dark_text_1' : 'dark:bg-dark_bg_3 text-dark_text_2'}`}>
                {aiEnabled ? 'مفعّل' : 'معطّل'}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <div className="rounded-lg border dark:border-dark_border_1 dark:bg-dark_bg_3 p-3">
                <p className="text-dark_text_2 text-sm">الذكاء الاصطناعي العام</p>
                <p className="text-dark_text_1 mt-1 font-semibold">{aiEnabled ? 'نشط في جميع الجلسات' : 'معطّل عالمياً'}</p>
              </div>
              <div className="rounded-lg border dark:border-dark_border_1 dark:bg-dark_bg_3 p-3">
                <p className="text-dark_text_2 text-sm">توزيع الجلسات</p>
                <p className="text-dark_text_1 mt-1 font-semibold">
                  AI On: {formatNumber(stats?.sessions?.aiEnabled)} | AI Off: {formatNumber(stats?.sessions?.aiDisabled)}
                </p>
              </div>
            </div>
          </div>

          <div className="card p-4">
            <h3 className="text-dark_text_1 font-medium">تفصيل الرسائل</h3>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <div className="rounded-lg p-3 dark:bg-dark_bg_3 border dark:border-dark_border_1">
                <p className="text-xs text-dark_text_2">المستخدمون</p>
                <p className="text-xl text-dark_text_1 font-semibold">{formatNumber(stats?.messages?.userMessages)}</p>
              </div>
              <div className="rounded-lg p-3 dark:bg-dark_bg_3 border dark:border-dark_border_1">
                <p className="text-xs text-dark_text_2">الذكاء الاصطناعي</p>
                <p className="text-xl text-dark_text_1 font-semibold">{formatNumber(stats?.messages?.aiMessages)}</p>
              </div>
              <div className="rounded-lg p-3 dark:bg-dark_bg_3 border dark:border-dark_border_1">
                <p className="text-xs text-dark_text_2">المديرون</p>
                <p className="text-xl text-dark_text_1 font-semibold">{formatNumber(stats?.messages?.adminMessages)}</p>
              </div>
              <div className="rounded-lg p-3 dark:bg-dark_bg_3 border dark:border-dark_border_1">
                <p className="text-xs text-dark_text_2">مع صور</p>
                <p className="text-xl text-dark_text_1 font-semibold">{formatNumber(stats?.messages?.withImages)}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <div className="card p-4">
            <h3 className="text-dark_text_1 font-medium mb-3">استخدام المنصات</h3>
            <div className="space-y-3">
              {Object.keys(sessionsByPlatform).length === 0 ? (
                <p className="text-sm text-dark_text_2">لا توجد بيانات للمنصات بعد.</p>
              ) : (
                Object.entries(sessionsByPlatform)
                  .sort((a, b) => b[1] - a[1])
                  .map(([platform, count]) => {
                    const value = Number(count) || 0;
                    const percentage = totalSessions > 0 ? Math.round((value / totalSessions) * 100) : 0;
                    return (
                      <div key={platform}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-dark_text_2">{formatPlatformName(platform)}</span>
                          <span className="text-dark_text_1 font-medium">{formatNumber(value)} ({percentage}%)</span>
                        </div>
                        <div className="w-full h-2 rounded-full dark:bg-dark_bg_3 overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-green_1 to-green_2" style={{ width: `${percentage}%` }} />
                        </div>
                      </div>
                    );
                  })
              )}
            </div>
          </div>

          <div className="xl:col-span-2 card p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-dark_text_1 font-medium">اتجاه الرسائل اليومية (آخر 7 أيام)</h3>
              <span className="text-xs text-dark_text_2">تحديث تلقائي كل 30 ثانية</span>
            </div>
            <div className="space-y-3 mt-3">
              {dailyTrend.length === 0 ? (
                <p className="text-sm text-dark_text_2">لا توجد بيانات للاتجاه بعد.</p>
              ) : (
                dailyTrend.map((item) => {
                  const count = Number(item.count) || 0;
                  const width = maxTrend > 0 ? Math.max(4, Math.round((count / maxTrend) * 100)) : 0;
                  const dateLabel = item.date ? new Date(item.date).toLocaleDateString() : 'N/A';
                  return (
                    <div key={`${item.date}-${item.count}`}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-dark_text_2">{dateLabel}</span>
                        <span className="text-dark_text_1 font-medium">{formatNumber(count)}</span>
                      </div>
                      <div className="w-full h-2.5 rounded-full dark:bg-dark_bg_3 overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-cyan-400 to-blue-500" style={{ width: `${width}%` }} />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <div className="card p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-dark_text_1 font-medium">أعلى الجلسات</h3>
              <button onClick={() => navigate('/app/inbox')} className="text-xs text-green_1 hover:text-green_2">عرض الكل</button>
            </div>
            <div className="space-y-2 mt-3">
              {topSessions.length === 0 ? (
                <p className="text-sm text-dark_text_2">لا توجد جلسات متاحة.</p>
              ) : (
                topSessions.slice(0, 6).map((session, index) => (
                  <button
                    key={session.sessionId}
                    onClick={() => navigate(`/app/inbox/${session.sessionId}`)}
                    className="w-full rounded-lg border dark:border-dark_border_1 dark:bg-dark_bg_3 p-3 hover:dark:bg-dark_hover_1 text-start transition"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="w-7 h-7 rounded-md bg-gradient-to-r from-green_1 to-green_2 text-white text-xs flex items-center justify-center font-semibold">
                          {index + 1}
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm text-dark_text_1 truncate">{session.userName || session.sessionId}</p>
                          <p className="text-xs text-dark_text_2">{formatPlatformName(session.platform)}</p>
                        </div>
                      </div>
                      <span className="text-xs px-2 py-1 rounded-full dark:bg-dark_bg_4 text-dark_text_1">{formatNumber(session.messageCount)}</span>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-dark_text_1 font-medium">الجلسات الأخيرة</h3>
              <button onClick={() => navigate('/app/inbox')} className="text-xs text-green_1 hover:text-green_2">عرض الكل</button>
            </div>
            <div className="space-y-2 mt-3">
              {recentSessions.length === 0 ? (
                <p className="text-sm text-dark_text_2">لا توجد جلسات حديثة.</p>
              ) : (
                recentSessions.map((session) => (
                  <button
                    key={session.sessionId}
                    onClick={() => navigate(`/app/inbox/${session.sessionId}`)}
                    className="w-full rounded-lg border dark:border-dark_border_1 dark:bg-dark_bg_3 p-3 hover:dark:bg-dark_hover_1 text-start transition"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm text-dark_text_1 truncate">
                          {session.platformUserName || session.platformUsername || session.name || 'عميل'}
                        </p>
                        <p className="text-xs text-dark_text_2 truncate">
                          {session.lastMessage?.content || 'لا توجد معاينة للرسالة'}
                        </p>
                      </div>
                      <div className="text-end shrink-0">
                        <p className="text-xs text-dark_text_2">{relativeTime(session.lastMessage?.timestamp)}</p>
                        {session.unreadCount > 0 && (
                          <span className="inline-flex mt-1 px-2 py-0.5 rounded-full bg-green_1 text-white text-[11px]">
                            {session.unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="card p-4">
          <h3 className="text-dark_text_1 font-medium mb-3">إجراءات سريعة</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <button onClick={() => navigate('/app/inbox')} className="px-4 py-3 rounded-lg bg-green_1 hover:bg-green_2 text-white font-medium">
              فتح صندوق الوارد
            </button>
            <button onClick={() => navigate('/app/platform-pages')} className="px-4 py-3 rounded-lg dark:bg-dark_bg_3 hover:dark:bg-dark_hover_1 border dark:border-dark_border_1 text-dark_text_1 font-medium">
              إدارة صفحات المنصات
            </button>
            <button onClick={() => navigate('/app/settings')} className="px-4 py-3 rounded-lg dark:bg-dark_bg_3 hover:dark:bg-dark_hover_1 border dark:border-dark_border_1 text-dark_text_1 font-medium">
              فتح الإعدادات
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
